// ./api/studentAPI.js

import axiosInstance from '../utilities/axiosInstance';


const studentAPI = {
  // ============================================================================
  // STUDENT PROFILE MANAGEMENT
  // ============================================================================
  
  // Get all students
  getAllStudents: async (params = {}) => {
    try {
      const response = await axiosInstance.get('/api/student/list/all', { params });
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get students: ${error.message}` };
    }
  },

  // Create student
  createStudent: async (loginId, studentData) => {
    try {
      const response = await axiosInstance.post(`/api/student/${loginId}`, studentData);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to create student: ${error.message}` };
    }
  },

  // Get student by loginId
  getStudentByLoginId: async (loginId) => {
    try {
      const response = await axiosInstance.get(`/api/student/${loginId}`);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get student: ${error.message}` };
    }
  },

  // Update student
  updateStudent: async (loginId, updateData) => {
    try {
      const response = await axiosInstance.put(`/api/student/${loginId}`, updateData);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to update student: ${error.message}` };
    }
  },

  // Delete (deactivate) student
  deleteStudent: async (loginId, deletedBy) => {
    try {
      const response = await axiosInstance.delete(`/api/student/${loginId}`, {
        data: { deletedBy }
      });
      return response.data; // {success, message}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to delete student: ${error.message}` };
    }
  },

  // Login student
  loginStudent: async (loginId) => {
    try {
      const response = await axiosInstance.post(`/api/student/${loginId}/login`);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Login failed: ${error.message}` };
    }
  },

  // ============================================================================
  // COURSE ENROLLMENT
  // ============================================================================

  // Enroll in course
  enrollCourse: async (loginId, courseId) => {
    try {
      const response = await axiosInstance.post(`/api/student/${loginId}/enroll`, { courseId });
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to enroll: ${error.message}` };
    }
  },

  // Unenroll from course
  unenrollCourse: async (loginId, courseId) => {
    try {
      const response = await axiosInstance.post(`/api/student/${loginId}/unenroll`, { courseId });
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to unenroll: ${error.message}` };
    }
  },

  // ============================================================================
  // LAB SUBMISSIONS
  // ============================================================================

  // Submit lab answers
  submitLabAnswers: async (loginId, labData) => {
    try {
      const response = await axiosInstance.post(`/api/student/${loginId}/lab`, labData);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to submit lab: ${error.message}` };
    }
  },

  // Get lab progress for a course
  getLabProgress: async (loginId, courseId) => {
    try {
      const response = await axiosInstance.get(`/api/student/${loginId}/lab/${courseId}`);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get lab progress: ${error.message}` };
    }
  },

  // ============================================================================
  // QUIZ SUBMISSIONS
  // ============================================================================

  // Submit quiz answers
  submitQuizAnswers: async (loginId, quizData) => {
    try {
      const response = await axiosInstance.post(`/api/student/${loginId}/quiz`, quizData);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to submit quiz: ${error.message}` };
    }
  },

  // Get quiz progress for a course
  getQuizProgress: async (loginId, courseId) => {
    try {
      const response = await axiosInstance.get(`/api/student/${loginId}/quiz/${courseId}`);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get quiz progress: ${error.message}` };
    }
  },

  // ============================================================================
  // EXAM SUBMISSIONS
  // ============================================================================

  // Submit exam answers
  submitExamAnswers: async (loginId, examData) => {
    try {
      const response = await axiosInstance.post(`/api/student/${loginId}/exam`, examData);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to submit exam: ${error.message}` };
    }
  },

  // Get exam progress for a course
  getExamProgress: async (loginId, courseId) => {
    try {
      const response = await axiosInstance.get(`/api/student/${loginId}/exam/${courseId}`);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get exam progress: ${error.message}` };
    }
  },

  // ============================================================================
  // CAPSTONE SUBMISSIONS
  // ============================================================================

  // Submit capstone project
  submitCapstone: async (loginId, capstoneData) => {
    try {
      const response = await axiosInstance.post(`/api/student/${loginId}/capstone`, capstoneData);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to submit capstone: ${error.message}` };
    }
  },

  // Get capstone progress for a course
  getCapstoneProgress: async (loginId, courseId) => {
    try {
      const response = await axiosInstance.get(`/api/student/${loginId}/capstone/${courseId}`);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get capstone progress: ${error.message}` };
    }
  },

  // ============================================================================
  // GRADES
  // ============================================================================

  // Get course grade
  getCourseGrade: async (loginId, courseId) => {
    try {
      const response = await axiosInstance.get(`/api/student/${loginId}/grade/${courseId}`);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get grade: ${error.message}` };
    }
  },

  // Calculate grade
  calculateGrade: async (loginId, courseId) => {
    try {
      const response = await axiosInstance.post(`/api/student/${loginId}/grade/${courseId}/calculate`);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to calculate grade: ${error.message}` };
    }
  },

  // Get all grades
  getAllGrades: async (loginId) => {
    try {
      const response = await axiosInstance.get(`/api/student/${loginId}/grades`);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get grades: ${error.message}` };
    }
  },

  // ============================================================================
  // PROGRESS
  // ============================================================================

  // Get course progress
  getCourseProgress: async (loginId, courseId) => {
    try {
      const response = await axiosInstance.get(`/api/student/${loginId}/progress/${courseId}`);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get progress: ${error.message}` };
    }
  },

  // ============================================================================
  // SHOPPING CART
  // ============================================================================

  // Add to cart
  addToCart: async (loginId, cartItem) => {
    try {
      const response = await axiosInstance.post(`/api/student/${loginId}/cart`, cartItem);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to add to cart: ${error.message}` };
    }
  },

  // Get cart
  getCart: async (loginId) => {
    try {
      const response = await axiosInstance.get(`/api/student/${loginId}/cart`);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get cart: ${error.message}` };
    }
  },

  // Remove from cart
  removeFromCart: async (loginId, courseId) => {
    try {
      const response = await axiosInstance.delete(`/api/student/${loginId}/cart/${courseId}`);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to remove from cart: ${error.message}` };
    }
  },

  // ============================================================================
  // STRIPE PAYMENT
  // ============================================================================

  // Create Stripe checkout session
  createStripeCheckout: async (loginId, checkoutData) => {
    try {
      const response = await axiosInstance.post(`/api/student/${loginId}/stripe/checkout`, checkoutData);
      return response.data; // {success, data: {sessionId, url, publicKey}}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Checkout failed: ${error.message}` };
    }
  },

  // Create subscription
  createSubscription: async (loginId, subscriptionData) => {
    try {
      const response = await axiosInstance.post(
        `/api/student/${loginId}/stripe/subscription`,
        subscriptionData
      );
      return response.data;
    } catch (error) {
      return error?.response?.data || { 
        success: false, 
        message: `Failed to create subscription: ${error.message}` 
      };
    }
  },

  // Cancel subscription
  cancelSubscription: async (loginId, subscriptionId) => {
    try {
      const response = await axiosInstance.delete(`/api/student/${loginId}/subscription/${subscriptionId}`);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Cancel failed: ${error.message}` };
    }
  },

  // Get subscriptions
  getSubscriptions: async (loginId) => {
    try {
      const response = await axiosInstance.get(`/api/student/${loginId}/subscriptions`);
      return response.data; // {success, data: [subscriptions]}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to fetch subscriptions: ${error.message}` };
    }
  },

  // Validate coupon
  // validateCoupon: async (couponCode) => {
  //   try {
  //     const response = await axiosInstance.post(`/api/student/coupon/validate`, { couponCode });
  //     return response.data; // {success, data: {valid, percentOff, amountOff}}
  //   } catch (error) {
  //     return error?.response?.data || { success: false, error: `Coupon validation failed: ${error.message}` };
  //   }
  // },

  // Apply coupon
  // applyCoupon: async (loginId, couponCode) => {
  //   try {
  //     const response = await axiosInstance.post(`/api/student/${loginId}/coupon/apply`, { couponCode });
  //     return response.data; // {success, data: {originalTotal, discountAmount, newTotal, coupon}}
  //   } catch (error) {
  //     return error?.response?.data || { success: false, error: `Apply coupon failed: ${error.message}` };
  //   }
  // },

  // Refund payment
  refundPayment: async (loginId, paymentId, reason) => {
    try {
      const response = await axiosInstance.post(`/api/student/${loginId}/payment/${paymentId}/refund`, { reason });
      return response.data; // {success, data: {refundId, status, amount}}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Refund failed: ${error.message}` };
    }
  },

  // Refund to credit
  refundToCredit: async (loginId, paymentId) => {
    try {
      const response = await axiosInstance.post(`/api/student/${loginId}/payment/${paymentId}/refund-to-credit`);
      return response.data; // {success, data: {creditAdded, newBalance}}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Refund to credit failed: ${error.message}` };
    }
  },

  processRefund: async (loginId, paymentId, action) => {
    const response = await axiosInstance.post(
        `/api/student/${loginId}/payments/${paymentId}/refund`,
        { action }
    );
    return response.data;
  },

  // Get payment methods
  getPaymentMethods: async (loginId) => {
    try {
      const response = await axiosInstance.get(`/api/student/${loginId}/payment-methods`);
      return response.data; // {success, data: [payment methods]}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to fetch payment methods: ${error.message}` };
    }
  },

  // Add payment method
  addPaymentMethod: async (loginId, paymentMethodId) => {
    try {
      const response = await axiosInstance.post(`/api/student/${loginId}/payment-methods`, { paymentMethodId });
      return response.data; // {success, message}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to add payment method: ${error.message}` };
    }
  },

  // Process payment
  processPayment: async (loginId, paymentData) => {
    try {
      const response = await axiosInstance.post(`/api/student/${loginId}/payment`, paymentData);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Payment failed: ${error.message}` };
    }
  },

  // Get payment history
  getPaymentHistory: async (loginId) => {
    try {
      const response = await axiosInstance.get(`/api/student/${loginId}/payment`);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get payment history: ${error.message}` };
    }
  },

  getSubscriptionInvoiceUrl: async (loginId, subscriptionId) => {
    const response = await axiosInstance.get(
        `/api/student/${loginId}/subscription/${subscriptionId}/invoice-url`
    );
    return response.data;
  },
  
};

export default studentAPI;